import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, message, DatePicker, Button, Divider, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../services/api';
import { SlotPicker } from '../Scheduling/SlotPicker';
import { OpenSlot } from '../Scheduling/useSlotPicker';
import { useHasRole } from '../auth/RoleGuard';

const { Text } = Typography;

interface PlanSegment { date: string; start: string; end: string; minutes: number; }

interface PlanStartResult {
  segments: PlanSegment[];
  is_multi_day: boolean;
  start_time: string;
  end_time: string;
  /** The technician the server resolved the segments against — explicit
   *  technician_id if one was sent, otherwise whatever
   *  SchedulingService.pickAvailableTechnician auto-assigned. Must be threaded
   *  back into the /appointments/quick booking call unchanged so the booking
   *  lands on the SAME tech the segments were planned for (a second,
   *  independent auto-assign resolve on /quick could otherwise pick a
   *  different technician than the one shown in the plan preview). */
  technician_id: number;
}

interface QuickBookingDrawerProps {
  visible: boolean;
  workOrderId?: number;
  estimateId?: number;
  /** Optional technician to pre-filter availability for and pre-assign on
   *  booking — set when opened from the "Book a visit with {tech}?" prompt
   *  after assigning a technician on a work order. */
  preferredTechnicianId?: number;
  onClose: () => void;
  onBooked?: () => void;
}

export const QuickBookingDrawer: React.FC<QuickBookingDrawerProps> = ({
  visible, workOrderId, estimateId, preferredTechnicianId, onClose, onBooked,
}) => {
  // Any-time rollover booking (Task 7) is a staff-only power feature — the
  // quick-slot picker below stays available to everyone (owner/advisor/
  // technician/customer entry points all render this drawer).
  const isStaff = useHasRole(['owner', 'advisor']);
  const [drawerWidth, setDrawerWidth] = useState<number | string>(600);
  useEffect(() => {
    // '100%' not '100vw' — 100vw includes the scrollbar on desktop browsers,
    // overflowing the right-anchored panel off the left edge of the screen.
    const u = () => setDrawerWidth(window.innerWidth <= 768 ? '100%' : 600);
    u(); window.addEventListener('resize', u);
    return () => window.removeEventListener('resize', u);
  }, []);

  const qs = workOrderId ? `work_order_id=${workOrderId}` : `estimate_id=${estimateId}`;

  const fetchAvailability = useCallback(async (startDate: string, endDate: string) => {
    const techQs = preferredTechnicianId != null ? `&technician_id=${preferredTechnicianId}` : '';
    const res = await api.get(`/appointments/quick-availability?${qs}&start_date=${startDate}&end_date=${endDate}${techQs}`);
    return res.data;
  }, [qs, preferredTechnicianId]);

  const submit = useCallback(async (startTime: string, segments?: OpenSlot['segments']) => {
    await api.post('/appointments/quick', {
      work_order_id: workOrderId,
      estimate_id: estimateId,
      start_time: startTime,
      technician_id: preferredTechnicianId,
      // Thread a rollover quick-slot's server-derived segment plan through to
      // /quick so it books the actual multi-day plan instead of one
      // contiguous block from start_time through closed hours (BUG NEW-1).
      // /quick drops this unless the shop's allow_multi_day_booking flag is
      // ON, so it's safe to always send when present.
      ...(segments && segments.length > 0 ? { segments } : {}),
    });
    message.success('Appointment booked');
    onBooked?.(); onClose();
  }, [workOrderId, estimateId, preferredTechnicianId, onBooked, onClose]);

  // Any-time rollover booking (staff-only, Task 7): plan an exact requested
  // start via /appointments/plan-start (server resolves the rollover
  // segments for that instant, same engine as the quick-slot list uses),
  // then book those server-computed segments via /appointments/quick. This
  // is additive — it does not touch the quick-slot SlotPicker/submit above.
  const [anyTimeValue, setAnyTimeValue] = useState<Dayjs | null>(null);
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState<PlanStartResult | null>(null);
  const [booking, setBooking] = useState(false);

  // BUG NEW-2 (flag-OFF parity): quick-availability now echoes the shop's
  // allow_multi_day_booking flag as multi_day_enabled. The any-time field
  // must only render when it's ON — otherwise staff on a flag-OFF shop could
  // still produce/book a multi-day plan, violating the "flag-OFF stays
  // byte-for-byte identical" guarantee. Defaults to false (hidden) until the
  // flag is confirmed on; fetched independently of the quick-slot SlotPicker
  // (which has its own internal fetchAvailability call) so visibility is
  // known before the user interacts with either control.
  const [multiDayEnabled, setMultiDayEnabled] = useState(false);
  useEffect(() => {
    if (!visible || !isStaff) return;
    let cancelled = false;
    const today = dayjs().format('YYYY-MM-DD');
    fetchAvailability(today, today)
      .then((res: any) => { if (!cancelled) setMultiDayEnabled(!!res?.multi_day_enabled); })
      .catch(() => { if (!cancelled) setMultiDayEnabled(false); });
    return () => { cancelled = true; };
  }, [visible, isStaff, fetchAvailability]);

  const planStart = useCallback(async () => {
    if (!anyTimeValue) return;
    setPlanning(true);
    setPlan(null);
    try {
      // /appointments/plan-start only derives duration from an estimate_id —
      // it has no work_order_id field. Reuse quick-availability (already
      // wired above for both the WO and estimate paths) to resolve the
      // job's duration_minutes so any-time planning works from either entry
      // point.
      const day = anyTimeValue.format('YYYY-MM-DD');
      const avail = await fetchAvailability(day, day);
      const { data } = await api.post('/appointments/plan-start', {
        estimate_id: estimateId,
        technician_id: preferredTechnicianId,
        duration_minutes: avail?.duration_minutes,
        start_time: anyTimeValue.toISOString(),
      });
      setPlan(data);
    } catch (err: any) {
      message.error(err?.response?.data?.reason || 'Could not plan that start time');
    } finally {
      setPlanning(false);
    }
  }, [anyTimeValue, estimateId, preferredTechnicianId, fetchAvailability]);

  const bookPlanned = useCallback(async () => {
    if (!plan) return;
    setBooking(true);
    try {
      await api.post('/appointments/quick', {
        work_order_id: workOrderId,
        estimate_id: estimateId,
        start_time: plan.start_time,
        // Pin to the tech the plan was computed against. preferredTechnicianId
        // was already what plan-start resolved against when it was set
        // (explicit-tech case); when it's unset, plan.technician_id carries
        // whichever tech plan-start auto-assigned, and /quick must NOT
        // re-resolve independently and possibly land on a different tech.
        technician_id: preferredTechnicianId ?? plan.technician_id,
        segments: plan.segments,
      });
      message.success('Appointment booked');
      onBooked?.(); onClose();
    } finally {
      setBooking(false);
    }
  }, [plan, workOrderId, estimateId, preferredTechnicianId, onBooked, onClose]);

  return (
    <Drawer title="Schedule visit" open={visible} onClose={onClose} width={drawerWidth} destroyOnHidden>
      {visible && <SlotPicker title="Pick a time" fetchAvailability={fetchAvailability} submit={submit} confirmLabel="Book" />}

      {visible && isStaff && multiDayEnabled && (
        <>
          <Divider />
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text strong>Any time</Text>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              value={anyTimeValue}
              onChange={(v) => { setAnyTimeValue(v); setPlan(null); }}
            />
            <Button onClick={planStart} loading={planning} disabled={!anyTimeValue}>
              Plan
            </Button>
            {plan && (
              <>
                <Text type="secondary">
                  {dayjs(plan.segments[0].start).format('ddd h:mmA')}
                  {' → '}
                  {dayjs(plan.segments[plan.segments.length - 1].end).format('ddd h:mmA')}
                </Text>
                <Button type="primary" onClick={bookPlanned} loading={booking} block>
                  Book
                </Button>
              </>
            )}
          </Space>
        </>
      )}
    </Drawer>
  );
};
